import { LitElement, css, html, nothing, unsafeCSS } from 'lit';
import { customElement } from 'lit/decorators.js';

import { NOTICE_COLUMN_MAX_PX } from '../../theme/notice.css';
import { uiState as uiStateProxy } from '../../UIState';
import { bindValtio } from '../../ValtioController';
import { SIDEBAR_OFFSET_FROM_CENTER, TOP_BAR_BOTTOM } from './layout';

// Import game components
import './components/aircraft-selector';
import './components/airmash-logo';
import './components/autopilot-banner';
import './components/btr-match-result';
import './components/chat-area';
import './components/cruise-selector';
import './components/ctf-match-result';
import './components/death-notice';
import './components/debug-popup';
import './components/game-log';
import './components/game-mode-status';
import './components/game-notification';
import './components/game-selector';
import './components/game-stats';
import './components/kill-notice';
import './components/level-up-notice';
import './components/loadout-panel';
import './components/menu-drawer';
import './components/menu-toolbar';
import './components/mobile-chat';
import './components/mini-leaderboard';
import './components/powerup-bar';
import './components/prowler-alert';
import './components/room-header';
import './components/score-big';
import './components/scoreboard-button';
import './components/spectator-overlay';
import './components/touch-controls';
import './components/upgrade-selector';
import './components/zoom-slider';

/**
 * Airmash Game - In-game screen container
 *
 * Top-level container for all in-game UI elements.
 * Children components handle specific UI areas (logo, menu, HUD, etc.).
 */
@customElement('airmash-game')
export class AirmashGame extends LitElement {
  static override styles = css`
    :host {
      display: block;
      /* Own the coordinate space for the absolutely-positioned children
         (.top-bar / .sidebar) instead of relying on a positioned ancestor.
         The host already fills the viewport, so this is a no-op visually. */
      position: relative;
      width: 100%;
      height: 100%;
    }

    .sidebar {
      position: absolute;
      /* Legacy's 50% - 290px, but never above the line under the top bar: the
         sidebar is 468px tall, so on a viewport under 690px (a 1366x768 laptop
         shows about 657px) the Predator button reached the logo, overlapped
         it by 19px at 620px, and left the top of the screen under 580px.
         Clamped, it sits 13px under the bar and its bottom reaches 523px,
         which only a viewport shorter than that cuts. The game log hides
         below 870px, so it never shares the line. */
      top: max(
        calc(${unsafeCSS(TOP_BAR_BOTTOM)}px + env(safe-area-inset-top, 0px)),
        calc(50% - ${unsafeCSS(SIDEBAR_OFFSET_FROM_CENTER)}px)
      );
      /* Clear of the landscape notch (see .top-bar). */
      left: calc(12px + env(safe-area-inset-left, 0px));
      width: 44px;
    }

    .spacer {
      height: 14px;
    }

    .top-bar {
      position: absolute;
      /* Below the iPhone status bar in the installed app held upright, where
         black-translucent draws the page under it. The inset is 0 in Safari,
         in landscape and on desktop, so this only moves the bar there. The
         rows under the bar add the same inset to stay in step. */
      top: calc(14px + env(safe-area-inset-top, 0px));
      /* Shift clear of the landscape notch / safe area. env() is 0 on
         non-notched screens and in portrait (notch is on top there), so this
         only moves the bar on a notched phone in landscape — exactly where
         the logo was being clipped. */
      left: calc(14px + env(safe-area-inset-left, 0px));
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: 8px;
      z-index: 10;
      pointer-events: none;
      /* Leaves the right edge to the menu-toolbar. */
      max-width: calc(100% - 220px);
    }

    /* The top-bar popovers (loadout, zoom, desktop game-selector) are
       position: fixed with z-index 200+, but fixed doesn't escape this bar's
       stacking context — their z-index only orders them inside the bar, so the
       kill notice and game notifications (z 50) painted over an open popover.
       Lift the whole bar while one is open; closed, it stays at 10 so the
       chips keep sitting under notices and match results as before. */
    .top-bar.popover-open {
      z-index: 200;
    }

    /* Tighter gaps on portrait phones — the top-bar competes with the
       menu-toolbar (right edge) and three flex gaps at 8px each add up.
       4px keeps items readable without crowding the right side. The logo
       gets a little extra so its white chip doesn't visually merge with
       the dark game-selector chip when the rounded corners almost touch. */
    @media (orientation: portrait) and (max-width: 600px) {
      .top-bar {
        gap: 4px;
      }
      .top-bar > airmash-logo {
        margin-right: 4px;
      }
    }

    .top-bar-separator {
      width: 1px;
      height: 18px;
      background-color: var(--color-overlay-medium);
      flex: 0 0 auto;
    }

    /* The status banners, bottom centre: the prowler alert over the autopilot
       banner. Up top they shared the game notices' lanes (13% / 18%) on any
       screen under ~830px tall (most laptops, every landscape phone), and sat
       under the CTF flags. The banners' hosts are display: contents, so only
       a showing banner is a flex item and a lone one takes the bottom slot.
       The spectator controls use this spot too, never at the same time: both
       banners need you alive. */
    .status-stack {
      position: absolute;
      bottom: 20px;
      left: 50%;
      transform: translateX(-50%);
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      z-index: 15;
    }

    /* A portrait phone has no clear spot. The fire buttons own the bottom
       centre, and just above the touch cluster a short screen (Safari's bars
       cut it to ~664px) puts the banner on your own plane at the centre. So
       it sits up top at 88px, under game-mode-status's row (64px, ~19px tall).
       Under the compact rules below the notices stack in a column from
       124px, under the banner; only a viewport exactly 600px wide, portrait
       but not compact, still uses the 13% lane, which can meet it at 664. */
    @media (orientation: portrait) and (max-width: 600px) {
      .status-stack {
        top: calc(88px + env(safe-area-inset-top, 0px));
        bottom: auto;
      }
    }

    /* The transient notices. On a desktop each places itself where legacy
       did (a server message at 13% or 18%, the level-up at 22.5%, the kill at
       32%, the death at 50%) and this box is no box. On a compact viewport, a
       phone or a short window, those bands run into each other, so the box
       becomes a column from under the top bar and the notices stack in it in
       order: a server message, the level-up, the kill, the death. Each one's
       compact rules make it a block of the column. The portrait phone's
       status stack sits at 88px, so the column starts under it there. */
    .notice-lanes {
      display: contents;
    }

    @media (max-width: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px), (max-height: ${unsafeCSS(NOTICE_COLUMN_MAX_PX)}px) {
      .notice-lanes {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: absolute;
        top: calc(70px + env(safe-area-inset-top, 0px));
        left: 0;
        right: 0;
        pointer-events: none;
        z-index: 50;
      }
    }

    @media (orientation: portrait) and (max-width: 600px) {
      .notice-lanes {
        top: calc(124px + env(safe-area-inset-top, 0px));
      }
    }
  `;

  /** UI state — `isMobile` swaps the sidebar contents and adds the loadout +
   *  scoreboard chips to the left action bar. */
  private uiState = bindValtio(this, uiStateProxy);

  override render() {
    const { isMobile, openTopBarPanel } = this.uiState;

    return html`
      <div class="top-bar ${openTopBarPanel !== null ? 'popover-open' : ''}">
        <airmash-logo></airmash-logo>
        ${isMobile ? nothing : html`<game-selector></game-selector>`}
        <div class="top-bar-separator" aria-hidden="true"></div>
        <zoom-slider></zoom-slider>
        <!-- Mobile left action bar: the sidebar selectors and the scoreboard
             live here as toolbar chips, freeing the corners for touch controls. -->
        ${isMobile ? html`<loadout-panel></loadout-panel><scoreboard-button></scoreboard-button>` : nothing}
      </div>

      <!-- Mobile: the game-selector is panel-only (the logo triggers it), so
           it lives outside the flex row rather than holding a phantom slot. -->
      ${isMobile ? html`<game-selector></game-selector>` : nothing}

      <game-log></game-log>
      <game-stats></game-stats>
      <game-mode-status></game-mode-status>
      <score-big></score-big>
      <room-header></room-header>
      <mini-leaderboard></mini-leaderboard>

      <!-- Left sidebar (desktop only): plane / upgrade / cruise selectors. On
           mobile the loadout lives in the top-left toolbar and cruise moves into
           the bottom-right touch cluster, so the sidebar isn't rendered. -->
      ${
        isMobile
          ? nothing
          : html`
              <div class="sidebar">
                <aircraft-selector></aircraft-selector>
                <div class="spacer"></div>
                <upgrade-selector></upgrade-selector>
                <div class="spacer"></div>
                <cruise-selector></cruise-selector>
              </div>
            `
      }

      <menu-toolbar></menu-toolbar>
      <menu-drawer></menu-drawer>

      <btr-match-result></btr-match-result>
      <ctf-match-result></ctf-match-result>
      <chat-area></chat-area>
      ${isMobile ? html`<mobile-chat></mobile-chat>` : nothing}
      <debug-popup></debug-popup>
      <powerup-bar></powerup-bar>
      <div class="status-stack">
        <prowler-alert></prowler-alert>
        <autopilot-banner></autopilot-banner>
      </div>
      <spectator-overlay></spectator-overlay>
      <!-- The transient notices, in the column's order; see .notice-lanes -->
      <div class="notice-lanes">
        <game-notification></game-notification>
        <level-up-notice></level-up-notice>
        <kill-notice></kill-notice>
        <death-notice></death-notice>
      </div>

      <!-- On-screen touch controls (joystick + fire/special). Mobile only. -->
      ${isMobile ? html`<touch-controls></touch-controls>` : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'airmash-game': AirmashGame;
  }
}
